import axios from 'axios'
import { msalService } from '@/config/useAuth.js'

const apiClient = axios.create({
  baseURL: window.env?.VUE_APP_ETL_API_URL || 'https://localhost:7112/api/v1'
})

// Add auth token to all requests using EAP's auth system
const { getToken } = msalService()
apiClient.interceptors.request.use(async (config) => {
  const token = await getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: Handle errors
apiClient.interceptors.response.use(
  (successfulResponse) => successfulResponse,
  (failedRequest) => {
    // Handle 401 Unauthorized - token might be expired
    if (failedRequest.response?.status === 401) {
      console.error('Unauthorized: Token may be expired or invalid')
    }

    const errorMessage =
      failedRequest.response?.data?.detail ||
      failedRequest.response?.data?.message ||
      failedRequest.response?.statusText ||
      failedRequest.message ||
      'Network connection failed'
    return Promise.reject({
      errorMessage,
      httpStatusCode: failedRequest.response?.status,
    })
  }
)

export default {
  validateApiConnection: (
    apiBaseUrl,
    manualEndpoints = null,
    authConfig = null
  ) =>
    apiClient.post('/connections/test', {
      baseUrl: apiBaseUrl,
      manualEndpoints: manualEndpoints,
      authConfig: authConfig,
    }),

  validateAuthentication: (
    apiBaseUrl,
    authenticationConfiguration
  ) =>
    apiClient.post('/connections/auth/test', {
      baseUrl: apiBaseUrl,
      authConfig: authenticationConfiguration,
    }),

  retrieveUserConnections: () =>
    apiClient.get('/connections'),

  persistConnection: (connectionData) =>
    apiClient.post('/connections', connectionData),

  removeConnection: (connectionId) =>
    apiClient.delete(`/connections/${connectionId}`),

  createDataPipeline: (pipelineData) =>
    apiClient.post('/pipelines', pipelineData),

  getPipelines: (status = null) => {
    const params = status ? { status } : {}
    return apiClient.get('/pipelines', { params })
  },

  getPipeline: (pipelineId) =>
    apiClient.get(`/pipelines/${pipelineId}`),

  updatePipeline: (pipelineId, pipelineData) =>
    apiClient.put(`/pipelines/${pipelineId}`, pipelineData),

  removePipeline: (pipelineId) =>
    apiClient.delete(`/pipelines/${pipelineId}`),

  runPipeline: (pipelineId) =>
    apiClient.post(`/pipelines/${pipelineId}/execute`),

  retrieveSampleDataFromEndpoint: (
    connectionId,
    endpoint,
    recordCount = 10
  ) =>
    apiClient.post(`/connections/${connectionId}/data/sample`, {
      endpoint,
      recordCount,
    }),

  getAvailableTables: () =>
    apiClient.get('/load/tables'),

  configurePipelineDestination: (
    pipelineId,
    tableName,
    writeMode,
    createIfNotExists = false
  ) =>
    apiClient.post(`/load/pipelines/${pipelineId}/configure`, {
      tableName,
      writeMode,
      createIfNotExists,
    }),

  retrieveEndpointSchema: (baseUrl, endpoint) =>
    apiClient.post('/connections/schema', {
      baseUrl,
      endpoint,
    }),

  testSqlQuery: (query) =>
    apiClient.post('/load/query', { query }),

  getPipelineExecutions: (pipelineId) =>
    apiClient.get(`/pipelines/${pipelineId}/executions`),

  getExecutionDetails: (executionId) =>
    apiClient.get(`/executions/${executionId}`),

  cancelExecution: (executionId) =>
    apiClient.post(`/executions/${executionId}/cancel`),

  retryExecution: (executionId) =>
    apiClient.post(`/executions/${executionId}/retry`),

  queryData: (naturalLanguageQuery, limit = 100) =>
    apiClient.post('/data/query', {
      query: naturalLanguageQuery,
      limit: limit,
    }),
}
